import { Observable } from 'rxjs';

export class FileReaderHelper {
  /**
   * Reads a file and returns its name and base64-encoded data as an Observable.
   *
   * @param {File} file - The file to be read.
   * @returns {Observable<{ name: string; data: string }>} Observable emitting an object with the normalized file name and base64 data string.
   *
   * @example
   * FileReaderHelper.readFile(file).subscribe(({ name, data }) => {
   *   // use name and data
   * });
   */
  static readFile(file: File): Observable<{ name: string; data: string }> {
    return new Observable(subscriber => {
      const reader = new FileReader();

      reader.onload = (event): void => {
        subscriber.next({ name: this.normalizeFileName(file.name), data: this.getStringData(event) });
        subscriber.complete();
      };

      reader.onerror = (error): void => subscriber.error(error);

      reader.readAsDataURL(file);
    });
  }

  static getStringData(event: ProgressEvent<FileReader>): string {
    const [, stringData] = event.target.result.toString().split(',');
    return stringData;
  }

  static normalizeFileName(fileName: string): string {
    const name = fileName.slice(0, fileName.lastIndexOf('.')).replace(/[^a-zA-Z0-9]/g, '');
    const extension = fileName.slice(fileName.lastIndexOf('.'));
    return `${name}${extension}`;
  }

  /**
   * Resizes an image file to fit within the specified maximum width and height, preserving aspect ratio.
   *
   * @param {File} file - The original image file to resize. Must be a valid image type (e.g., JPEG, PNG).
   * @param {number} maxWidth - The maximum width (in pixels) for the resized image.
   * @param {number} maxHeight - The maximum height (in pixels) for the resized image.
   * @returns {Promise<File>} A Promise that resolves to a new File object containing the resized image data.
   *
   * @example
   * const resized = await imageResizeUtil(file, 800, 600);
   * // resized is a File with dimensions <= 800x600
   */
  static imageResizeUtil = (file: File, maxWidth: number, maxHeight: number): Promise<File> => {
    return new Promise((resolve, reject) => {
      const img = new Image();
      const reader = new FileReader();

      reader.onload = (e: ProgressEvent<FileReader>): void => {
        img.src = e.target.result as string;
      };

      img.onload = (): void => {
        let { width, height } = img;

        if (width > maxWidth || height > maxHeight) {
          const scale = Math.min(maxWidth / width, maxHeight / height);
          width = width * scale;
          height = height * scale;
        }

        const canvas = document.createElement('canvas');
        canvas.width = width;
        canvas.height = height;

        const ctx = canvas.getContext('2d');
        ctx?.drawImage(img, 0, 0, width, height);

        canvas.toBlob(
          blob => {
            if (blob) {
              const resizedFile = new File([blob], file.name, { type: file.type });
              resolve(resizedFile);
            } else {
              reject(new Error('Can not resize image'));
            }
          },
          file.type,
          0.9
        );
      };

      reader.onerror = (err): void => reject(err);
      reader.readAsDataURL(file);
    });
  };
}
